import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Anchor,
  CheckCircle2,
  CloudLightning,
  Download,
  ExternalLink,
  Flame,
  LifeBuoy,
  Navigation,
  Printer,
  Radio,
  ShieldCheck,
  Ship,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const beforeSailChecks = [
  "Check current forecast, wind, waves, visibility, and tide windows.",
  "Confirm route, alternates, fuel range, and safe return point.",
  "Brief every passenger on life jackets, VHF, MOB, and fire response.",
  "Inspect bilge, engine, steering, batteries, navigation lights, and anchor.",
  "Confirm flares, first aid, EPIRB/PLB, VHF, and throwable flotation are reachable.",
  "Share your float plan, ETA, crew count, and emergency contact ashore.",
];

const emergencyCards = [
  { title: "MOB", risk: "Critical", icon: LifeBuoy, color: "border-red-500 bg-red-50 text-red-800", steps: ["Shout and point", "Throw flotation", "Mark GPS", "Call MAYDAY if needed"] },
  { title: "Fire", risk: "Critical", icon: Flame, color: "border-orange-500 bg-orange-50 text-orange-800", steps: ["Stop fuel/engine", "Use correct extinguisher", "Move crew upwind", "Prepare MAYDAY"] },
  { title: "Collision", risk: "High", icon: Ship, color: "border-amber-500 bg-amber-50 text-amber-800", steps: ["Check injuries", "Assess flooding", "Broadcast position", "Prepare abandon-ship kit"] },
  { title: "Engine failure", risk: "Medium", icon: Wrench, color: "border-blue-500 bg-blue-50 text-blue-800", steps: ["Control drift", "Check fuel/cooling", "Deploy anchor if needed", "Send PAN-PAN"] },
  { title: "Severe weather", risk: "High", icon: CloudLightning, color: "border-purple-500 bg-purple-50 text-purple-800", steps: ["Life jackets on", "Reef/slow down", "Secure deck", "Head to safe shelter"] },
];

const equipmentRows = [
  { scope: "Day trip / protected water", items: "Life jackets, throwable flotation, VHF/phone, anchor, first aid, bilge pump, navigation lights." },
  { scope: "Coastal trip", items: "Add flares, sound signal, spare fuel, tool kit, charts/offline map, tow line, emergency water." },
  { scope: "Offshore / night passage", items: "Add EPIRB/PLB, liferaft, harnesses, jacklines, radar reflector, backup GPS, full distress kit." },
];

const firstMinuteSteps = [
  "Stop, breathe, and put one person in command.",
  "Protect people first: life jackets, headcount, injuries, and MOB watch.",
  "Stabilize the boat: reduce speed, stop fuel/electrics if needed, control flooding/fire.",
  "Mark position and send the correct call: MAYDAY, PAN-PAN, or SÉCURITÉ.",
  "Prepare recovery, shelter, tow, or abandon-ship actions before conditions worsen.",
];

const explainers = [
  { title: "Flares", text: "Use only when a rescue vessel or aircraft can see you; keep them dry and within reach." },
  { title: "VHF Mayday", text: "Call on Channel 16 with vessel name, position, distress type, people aboard, and assistance needed." },
  { title: "Life jackets", text: "Wear them before risk rises; assign properly fitted jackets to every person before departure." },
  { title: "EPIRB", text: "Activate during grave distress when you need satellite rescue alerting; keep it with people, not the boat." },
  { title: "Bilge pumps", text: "Test automatic and manual pumps before leaving and know where the switches and strainers are." },
];

const relatedLinks = [
  { label: "Weather", to: "weather", icon: CloudLightning },
  { label: "Navigation", to: "navigation", icon: Navigation },
  { label: "SOS tools", to: "#distress-calls", icon: AlertTriangle },
  { label: "Route planning", to: "trips", icon: Anchor },
  { label: "Port entry", to: "port-entry", icon: Ship },
];

const checklistText = `Marstick Safety Checklist\n\nBefore You Sail\n- ${beforeSailChecks.join("\n- ")}\n\nFirst 60 Seconds\n- ${firstMinuteSteps.join("\n- ")}\n\nWeather No-Go Signals\n- Thunderstorms, lightning, poor visibility, strong gusts, steep waves, unsafe tide windows, or crew fatigue.`;

export default function SafetyPreparednessSections({ lang = "en" }) {
  const prefix = `/${lang}`;
  const downloadHref = `data:text/plain;charset=utf-8,${encodeURIComponent(checklistText)}`;

  return (
    <section className="space-y-6" aria-label="Safety preparedness guide">
      <div className="rounded-[2rem] border border-red-200 bg-red-50 p-5 text-red-950 shadow-lg shadow-red-900/5 md:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="max-w-3xl">
            <Badge className="border-red-300 bg-white text-red-700">Weather risk warning</Badge>
            <h2 className="mt-3 font-heading text-3xl font-black md:text-4xl">Do Not Depart When Risk Is Rising</h2>
            <p className="mt-3 text-base leading-7 text-red-900">
              Stay in port when thunderstorms, lightning, fast-building gusts, poor visibility, steep confused seas, unsafe tide windows, equipment faults, or crew fatigue make recovery harder than departure.
            </p>
          </div>
          <AlertTriangle className="h-12 w-12 shrink-0 text-red-600" aria-hidden="true" />
        </div>
      </div>

      <Card className="border-border bg-card">
        <CardContent className="p-5 md:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-primary">Before You Sail</p>
              <h2 className="mt-2 font-heading text-3xl font-black md:text-4xl">Quick Pre-Departure Checklist</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" className="rounded-2xl">
                <a href={downloadHref} download="marstick-safety-checklist.txt"><Download className="h-4 w-4" /> Download</a>
              </Button>
              <Button type="button" variant="outline" className="rounded-2xl" onClick={() => window.print()}>
                <Printer className="h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {beforeSailChecks.map((item) => (
              <div key={item} className="flex gap-3 rounded-2xl border border-border bg-background/70 p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                <p className="text-sm leading-6 text-foreground">{item}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <div>
          <p className="text-sm font-black uppercase tracking-[0.2em] text-primary">Emergency action cards</p>
          <h2 className="mt-2 font-heading text-3xl font-black md:text-4xl">Know the First Moves</h2>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {emergencyCards.map(({ title, risk, icon: Icon, color, steps }) => (
            <div key={title} className={`rounded-2xl border-2 p-4 ${color}`}>
              <div className="flex items-center justify-between gap-2">
                <Icon className="h-6 w-6" aria-hidden="true" />
                <span className="rounded-full bg-white/80 px-2 py-1 text-xs font-black">{risk}</span>
              </div>
              <h3 className="mt-3 text-xl font-black">{title}</h3>
              <ul className="mt-3 space-y-1 text-sm leading-6">
                {steps.map((step) => <li key={step}>• {step}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-5 md:p-6">
          <p className="text-sm font-black uppercase tracking-[0.2em] text-primary">Safety equipment required</p>
          <h2 className="mt-2 font-heading text-3xl font-black md:text-4xl">Match Gear to Trip Distance</h2>
          <div className="mt-5 grid gap-3">
            {equipmentRows.map((row) => (
              <div key={row.scope} className="rounded-2xl border border-border bg-background/70 p-4">
                <h3 className="font-black text-foreground">{row.scope}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{row.items}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
        <Card>
          <CardContent className="p-5 md:p-6">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-primary">First 60 seconds</p>
            <h2 className="mt-2 font-heading text-3xl font-black md:text-4xl">Take Control Fast</h2>
            <ol className="mt-5 space-y-3">
              {firstMinuteSteps.map((step, index) => (
                <li key={step} className="flex gap-3 text-sm leading-6">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary text-xs font-black text-primary-foreground">{index + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 md:p-6">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-primary">Short explainers</p>
            <h2 className="mt-2 font-heading text-3xl font-black md:text-4xl">Core Safety Gear Basics</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {explainers.map((item) => (
                <div key={item.title} className="rounded-2xl border border-border bg-background/70 p-4">
                  <h3 className="font-black text-foreground">{item.title}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-5 md:p-6">
          <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr] lg:items-start">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-primary">Related tools</p>
              <h2 className="mt-2 font-heading text-3xl font-black md:text-4xl">Continue Your Safety Workflow</h2>
              <div className="mt-5 flex flex-wrap gap-2">
                {relatedLinks.map(({ label, to, icon: Icon }) => {
                  const href = to.startsWith("#") ? to : `${prefix}/${to}`;
                  const isAnchor = to.startsWith("#");
                  const content = <><Icon className="h-4 w-4" /> {label} <ExternalLink className="h-3.5 w-3.5" /></>;
                  return isAnchor ? (
                    <a key={label} href={href} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-bold text-foreground hover:border-primary hover:text-primary">{content}</a>
                  ) : (
                    <Link key={label} to={href} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-bold text-foreground hover:border-primary hover:text-primary">{content}</Link>
                  );
                })}
              </div>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-950">
              <div className="flex items-center gap-2 font-black"><ShieldCheck className="h-5 w-5" /> Reviewed for Maritime Safety</div>
              <p className="mt-2 text-sm leading-6">Last updated: 24 June 2026. Use this page as a planning aid, not a replacement for official coast guard rules, local regulations, vessel manuals, certified training, or skipper judgment.</p>
              <p className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-emerald-800">Sources: COLREGs principles, standard VHF distress procedures, coastal safety guidance, and manufacturer equipment instructions.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}